import type { DartThrow } from "./types.js";
import { DEFAULT_AUTODARTS_URL } from "./types.js";

export interface AutodartsThrow {
  segment: number;
  multiplier: 1 | 2 | 3;
  name: string;
}

export interface AutodartsBoardState {
  event: string;
  status: string;
  throws: AutodartsThrow[];
}

type RawSegment = {
  name?: string;
  number?: number;
  multiplier?: number;
  bed?: string;
};

type RawThrow = {
  segment?: RawSegment;
  name?: string;
  number?: number;
  multiplier?: number;
};

export function normalizeAutodartsUrl(raw: string | null | undefined): string {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return "";
  try {
    let value = trimmed.replace(/\/+$/, "");
    if (!/^https?:\/\//i.test(value)) value = `http://${value}`;
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "";
    if (!url.port) url.port = "3180";
    return url.origin;
  } catch {
    return "";
  }
}

export function autodartsStateUrl(raw: string | null | undefined): string {
  const origin = normalizeAutodartsUrl(raw) || DEFAULT_AUTODARTS_URL;
  return `${origin}/api/state`;
}

function asMultiplier(value: unknown, fallback: 1 | 2 | 3 = 1): 1 | 2 | 3 {
  const n = Number(value);
  if (n === 2 || n === 3) return n;
  return fallback;
}

function dartFromName(name: string): AutodartsThrow | null {
  const raw = name.trim();
  if (!raw) return null;
  const upper = raw.replace(/\s+/g, "");
  if (/^(miss|outside|out)$/i.test(upper)) {
    return { segment: 0, multiplier: 1, name: "Miss" };
  }
  if (/^(dbull|db|bullseye|50)$/i.test(upper)) {
    return { segment: 25, multiplier: 2, name: "DBull" };
  }
  if (/^(bull|sbull|sb|25)$/i.test(upper)) {
    return { segment: 25, multiplier: 1, name: "Bull" };
  }
  const m = /^(S|D|T)?(\d{1,2})$/i.exec(upper);
  if (!m) return null;
  const prefix = (m[1] ?? "S").toUpperCase();
  const segment = Number(m[2]);
  if (segment === 25 || segment === 50) {
    return dartFromName(segment === 50 ? "DBull" : "Bull");
  }
  if (!Number.isInteger(segment) || segment < 0 || segment > 20) return null;
  if (segment === 0) return { segment: 0, multiplier: 1, name: "Miss" };
  const multiplier: 1 | 2 | 3 = prefix === "T" ? 3 : prefix === "D" ? 2 : 1;
  return { segment, multiplier, name: `${prefix === "S" ? "" : prefix}${segment}` || String(segment) };
}

export function dartFromAutodartsSegment(raw: RawSegment | RawThrow | null | undefined): AutodartsThrow | null {
  if (!raw || typeof raw !== "object") return null;
  const nested = "segment" in raw && raw.segment && typeof raw.segment === "object" ? raw.segment : raw;
  const name = String(nested.name ?? ("name" in raw ? raw.name : "") ?? "").trim();
  const fromName = name ? dartFromName(name) : null;
  const number = Number(nested.number ?? ("number" in raw ? raw.number : NaN));
  const multiplierRaw = nested.multiplier ?? ("multiplier" in raw ? raw.multiplier : undefined);
  const bed = String(nested.bed ?? "").toLowerCase();

  if (Number.isInteger(number) && (number === 0 || (number >= 1 && number <= 20) || number === 25 || number === 50)) {
    if (number === 0) return { segment: 0, multiplier: 1, name: name || "Miss" };
    if (number === 50) return { segment: 25, multiplier: 2, name: name || "DBull" };
    let multiplier = asMultiplier(multiplierRaw, fromName?.multiplier ?? 1);
    if (number === 25) {
      if (bed.includes("double") || multiplier === 2 || multiplier === 3) multiplier = 2;
      else multiplier = 1;
      return { segment: 25, multiplier, name: name || (multiplier === 2 ? "DBull" : "Bull") };
    }
    if (bed.includes("triple")) multiplier = 3;
    else if (bed.includes("double")) multiplier = 2;
    else if (bed.includes("single")) multiplier = 1;
    if (multiplierRaw === 0) return { segment: 0, multiplier: 1, name: name || "Miss" };
    return { segment: number, multiplier, name: name || `${multiplier === 3 ? "T" : multiplier === 2 ? "D" : ""}${number}` };
  }

  return fromName;
}

export function parseAutodartsState(raw: unknown): AutodartsBoardState {
  const obj = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const event = String(obj.event ?? obj.status ?? "").trim();
  const status = String(obj.status ?? obj.event ?? "").trim();
  const list = Array.isArray(obj.throws)
    ? obj.throws
    : Array.isArray(obj.darts)
      ? obj.darts
      : [];
  const throws: AutodartsThrow[] = [];
  for (const item of list) {
    const dart = dartFromAutodartsSegment(item as RawThrow);
    if (dart) throws.push(dart);
  }
  return { event, status, throws };
}

export function autodartsSignature(throws: AutodartsThrow[]): string {
  return throws.map((d) => `${d.segment}x${d.multiplier}`).join(",");
}

export function isAutodartsTakeout(state: AutodartsBoardState): boolean {
  const key = `${state.event} ${state.status}`.toLowerCase();
  return /\btakeout\b|\btake.?out\b|\bpull\b|\breset\b/.test(key);
}

export function autodartsStatusLabel(state: AutodartsBoardState | null, reachable: boolean): string {
  if (!reachable) return "Nicht erreichbar";
  if (!state) return "Verbinde…";
  const key = `${state.event} ${state.status}`.toLowerCase();
  if (/\btakeout\b|\btake.?out\b|\bpull\b/.test(key)) return "Darts ziehen";
  if (/\bthrow\b|\bhit\b/.test(key)) return "Treffer";
  if (/\bstop/.test(key)) return "Erkennung aus";
  return "Bereit";
}
